import React, { useEffect, useState } from 'react';
import { AnimatedDashboard, ErrorToast } from '../../components';
import { CaptionText, H2, H3, H4, P1, P2 } from '../../styles/FontStyles';
import { Box, Button, Flexed } from '../../styles/GlobalComponents';
import {
  EarnBet50Percent,
  EarnBetHalfAmount,
  EarnBetInput,
  EarnBetInputBox,
  EarnBetInputBox2,
  EarnBetInputBox3
} from './EarnBet.styled';
import { PurchaseEarnBetSteps } from './EarnBet.types';
import { PaymentInit } from './Payment/PaymentInit';
import { Desktop } from '../../App';
import { Mobile } from '../Kyc/KycStyled';
import earnBetBonusImage from '../../assets/images/backoffice/earnBet/earnBetBonusImage.png';
import earnBetBonusImageMobile from '../../assets/images/backoffice/earnBet/earnBetBonusImageMobile.png';

import earnBetBanner from '../../assets/images/backoffice/earnBet/earnBetBanner.png';
import earnBetBannerMobile from '../../assets/images/backoffice/earnBet/earnBetBannerMobile.png';
import { useDispatch } from 'react-redux';
import { checkEarnBetUser } from '../Earnings/TranferToEarnBet/Transfer.actions';
import { useAppSelector } from '../../app/hooks';
import axios from 'axios';
import { ProgressBar } from './ProgressBar';
import { formatUSD } from '../../helpers/helperFunctions';
import { initEarnings } from '../Earnings/Earnings.actions';
import { lightTheme } from '../../styles/GlobalStyles';

const earnBetRegisterUrl = process.env.REACT_APP_EARNBET_REGISTER_URL;

export const EarnBet = () => {
  const numberOfPage = 1;
  const countPerPage = 50;

  const [progress, setProgress] = useState<number>(1500);

  const [modal, setModal] = useState<boolean>(false);
  const [transferStep, setTransferStep] = useState<PurchaseEarnBetSteps>(
    PurchaseEarnBetSteps.CONFIRM_DEPOSIT
  );

  const [inputValue, setInputValue] = useState<string>('1500');

  const dispatch = useDispatch();

  const { earnbetUserExists, hasPaidDeposit } = useAppSelector(
    (state) => state.earnings
  );

  const { user } = useAppSelector((state) => state.auth.userInfo);

  const getEarnBetBonusPercentage = (deposit: number) => {
    if (deposit >= 5000) return 100;
    if (deposit >= 1500) return 100;
    if (deposit >= 100) return 100;
    if (deposit >= 0) return 100;
    return 0;
  };

  const earnBetBonusPercentage = getEarnBetBonusPercentage(progress);
  const earnBetBonusAmount = (progress * earnBetBonusPercentage) / 100;

  const generateEarnbetToken = async () => {
    try {
      const { data } = await axios.post(`/earnbet/onetime/generate`);

      window.open(
        `${earnBetRegisterUrl}/${data.data.token}/${user.id}`,
        '_blank'
      );
    } catch (e) {
      ErrorToast();
    }
  };

  useEffect(() => {
    setInputValue(progress.toString());
  }, [progress]);

  useEffect(() => {
    dispatch(checkEarnBetUser());
    dispatch(initEarnings({ numberOfPage, countPerPage }));
  }, [dispatch]);

  const renderButtonText = () => {
    if (earnbetUserExists) {
      if (hasPaidDeposit) {
        return 'Initial deposit made';
      } else {
        return 'Complete Deposit';
      }
    } else {
      return 'Register on Earnbet';
    }
  };

  const handleProgressChange = ({
    target
  }: React.ChangeEvent<HTMLInputElement>) => {
    const val = target.value;

    // ✅ Regex: match numbers with max 2 decimal places
    if (!/^(\d+(\.\d{0,2})?)?$/.test(val)) return;

    setInputValue(val);

    if (val === '') return setProgress(0);

    const numberVal = Number(val);
    if (isNaN(numberVal)) return;

    const clamped = Math.max(0, Math.min(50000, numberVal));
    const rounded = Math.round(clamped * 100) / 100;

    setProgress(rounded);
  };
  return (
    <AnimatedDashboard zoom key={'earn-bet'}>
      <>
        <Desktop>
          <section>
            <img
              src={earnBetBanner}
              alt='EarnBet'
              style={{ width: '100%', marginBottom: 30 }}
            />
          </section>
        </Desktop>
        <Mobile>
          <section>
            <img
              src={earnBetBannerMobile}
              alt='EarnBet'
              style={{ width: '100%', marginBottom: 30 }}
            />
          </section>
        </Mobile>
        <H3 color='#008F94'>Your Opportunity to Become the House</H3>
        <P1>
          Established in <b>2018, EarnBet</b> has earned its reputation as one
          of the most trusted and transparent names in the online casino
          industry. With over <b>$2 Billion in payouts</b> distributed to
          players worldwide, EarnBet stands as a proven, secure, and rewarding
          platform for both casual gamers and high rollers alike.
          <br />
          <br />
        </P1>
        <P1 bold color='#008F94'>
          Every Bet Gives Back
          <br />
        </P1>
        <P1 mb='40'>
          Every time you place a bet on EarnBet, you’re playing on a platform
          that values community and long-term sustainability. What makes EarnBet
          truly unique is its <b>profit-sharing model</b> — where a portion of
          the house edge is distributed to <b>Gaming Hub NFT holders</b> and{' '}
          <b>Big Whale Package owners.</b> These community stakeholders earn
          from the platform’s success, making EarnBet one of the only casinos
          where the house is partly owned by the community.
          <br />
          <br />
          With a history of <b>secure, fast payouts</b> and a track record of
          over <b>$2B in total winnings,</b> EarnBet is where credibility meets
          opportunity.
          <br />
          <br />
          Join the platform that’s changing the way online gaming works.
          <br />
          <br />
          <b>EarnBet – Where Every Bet Matters.</b>
        </P1>
        <Desktop>
          <img
            src={earnBetBonusImage}
            alt='EarnBet'
            style={{ width: '100%', borderRadius: 12, marginBottom: 40 }}
          />
        </Desktop>
        <Mobile>
          <img
            src={earnBetBonusImageMobile}
            alt='EarnBet'
            style={{ width: '100%', borderRadius: 12, marginBottom: 40 }}
          />
        </Mobile>

        <H2>Initial Deposit Amount</H2>
        <ProgressBar current={progress} max={50000} onChange={setProgress} />

        <Flexed gap='16px' rowSM>
          <Flexed width='50%' widthSM='100%'>
            <EarnBetInputBox>
              <EarnBetInput
                type='number'
                step='0.01' // ✅ enables 2 decimals
                value={inputValue}
                onChange={handleProgressChange}
              />
              <H2 color='#063549'>USDT</H2>
            </EarnBetInputBox>
          </Flexed>
          <Flexed width='50%' widthSM='100%'>
            <EarnBetInputBox2>
              <div>
                <H2 color='#063549'>
                  EarnBet <br /> Bonus
                </H2>
                <EarnBet50Percent>
                  {getEarnBetBonusPercentage(progress)}%
                </EarnBet50Percent>
              </div>
              <Flexed center gap='16px'>
                <EarnBetHalfAmount>
                  {formatUSD(earnBetBonusAmount)}
                </EarnBetHalfAmount>
                <H2 color='#063549'>USDT</H2>
              </Flexed>
            </EarnBetInputBox2>
          </Flexed>
        </Flexed>
        <EarnBetInputBox3>
          <div>
            <H2 color='#063549'>Total Amount</H2>
            <EarnBet50Percent>
              {formatUSD(progress)} + {formatUSD(earnBetBonusAmount)}
            </EarnBet50Percent>
          </div>
          <Flexed center gap='16px'>
            <EarnBetHalfAmount>
              {formatUSD(progress + earnBetBonusAmount)}
            </EarnBetHalfAmount>
            <H2 color='#063549'>USDT</H2>
          </Flexed>
          <Flexed column alignEnd>
            <Button
              negative
              w='291px'
              wSM='100%'
              mt='7'
              disabled={Number(progress) < 10 || hasPaidDeposit}
              onClick={() => {
                if (earnbetUserExists) {
                  if (!hasPaidDeposit) {
                    setModal(!modal);
                    setTransferStep(PurchaseEarnBetSteps.CONFIRM_DEPOSIT);
                  }
                } else {
                  generateEarnbetToken();
                }
              }}>
              {renderButtonText()}
            </Button>
            {hasPaidDeposit && (
              <CaptionText color={lightTheme.colors.neutral500} mt='10'>
                *All future deposits can be made directly on EarnBet
              </CaptionText>
            )}
          </Flexed>
        </EarnBetInputBox3>
        <Box gray>
          <H2 center>
            What is the{' '}
            <span style={{ color: '#17ADC1' }}>Rollover Requirement</span> to{' '}
            <br />
            withdraw from my initial deposit?
          </H2>
          <br />
          <br />
          <P1>
            When you make your <b>first deposit</b> on EarnBet, a{' '}
            <b>rollover requirement applies before withdrawals are allowed</b>.
            This requirement is calculated based on the{' '}
            <b>
              total amount of your initial deposit plus any EarnBet bonus
              received.
            </b>
            <br />
            <br />
            The <b>average rollover requirement is 30x</b>, but please note this
            is an <b>estimated average based on the house edge of each game.</b>{' '}
            Some games may contribute more or less toward the rollover depending
            on their individual house edge — meaning your actual required
            wagering amount may vary slightly depending on which games you play.
            <br />
            <br />
            For example, if you deposit $100 and receive a 25% bonus ($25), your
            total is $125. Based on the average 30x rollover, you would need to
            place approximately <b>$3,750</b> in total wagers before being
            eligible to withdraw. However, the exact requirement may vary
            slightly depending on the games you choose to play.
            <br />
            <br />
            <span style={{ color: '#17ADC1', fontWeight: 600 }}>
              Important Notes:
            </span>
            <ul>
              <li>
                The 30x rollover requirement applies{' '}
                <b>only to your first deposit and the associated bonus.</b>
              </li>
              <li>
                You <b>must complete</b> the wagering requirement before making
                any withdrawals.
              </li>
              <li>
                <b>
                  Any deposits made after your first one are not subject to a
                  rollover requirement.
                </b>{' '}
                You can withdraw those funds at any time without restrictions.
              </li>
            </ul>
            <br />
            To view your rollover requirement in detail, please log into your
            EarnBet account, click on Deposit, then select Balances. Under Wager
            Requirement, you will see your rollover amount.
            <br />
            <br />
            If you have any questions, feel free to reach out via EarnBet’s Live
            Chat.
          </P1>
        </Box>
      </>
      <PaymentInit
        modal={modal}
        setModal={setModal}
        price={progress}
        transferStep={transferStep}
        setTransferStep={setTransferStep}
      />
    </AnimatedDashboard>
  );
};
